import { trigger } from '@angular/animations';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  TrackByFunction,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, interval, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { ScreenService } from '../../services';
import { ClientConfigService } from '../../services/client-config/client-config.service';
import { HeroItemActionClickType, HeroItemViewModel } from '../hero-item/hero-item.view-model';
import { getDecrementAnimationMetadata, getIncrementAnimationMetadata, TRANSITION_TIME } from './hero-animations';
import { HeroViewModel } from './hero.view-model';

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const AUTO_PLAY_INTERVAL = 10 * 1000; // ms

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const HERO_CONTAINER_MAX_HEIGHT_XS = 768 * 1.053125; // 105.3125vw
const HERO_CONTAINER_MAX_WIDTH_XS = 768;
const HERO_CONTAINER_MAX_HEIGHT = 1080;
const HERO_CONTAINER_MAX_WIDTH = 1920;

@Component({
  selector: 'osn-shared-hero',
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.scss'],
  animations: [
    trigger('ltrAnimation', [getIncrementAnimationMetadata(false), getDecrementAnimationMetadata(false)]),
    trigger('rtlAnimation', [getIncrementAnimationMetadata(true), getDecrementAnimationMetadata(true)]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroComponent implements OnDestroy, OnInit {
  public get vm(): HeroViewModel {
    return this._vm;
  }

  @Input()
  public set vm(value: HeroViewModel) {
    this._vm = value;
  }
  @Output() public heroItemChangeByTheUser = new EventEmitter<{ heroItem: HeroItemViewModel }>();
  @Output() public heroItemButtonClick = new EventEmitter<{
    actionType: HeroItemActionClickType;
    heroItem: HeroItemViewModel;
    index: number;
    buttonText: string;
  }>();
  @Output() public heroItemGenreClick = new EventEmitter<{ heroItem: HeroItemViewModel; genreName: string; videoLength: string }>();

  // eslint-disable-next-line @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match
  private _vm: HeroViewModel;
  public slideCounter = 0;
  public prevIndex: number;
  public transitionInProgress = false;
  public isMobile$: Observable<boolean>;
  public isLarge$: Observable<boolean>;
  public loadingWidth$: Observable<number> = this.screenService.isXS$.pipe(
    map((isXS) => (isXS ? HERO_CONTAINER_MAX_WIDTH_XS : HERO_CONTAINER_MAX_WIDTH))
  );
  public loadingHeight$: Observable<number> = this.screenService.isXS$.pipe(
    map((isXS) => (isXS ? HERO_CONTAINER_MAX_HEIGHT_XS : HERO_CONTAINER_MAX_HEIGHT))
  );

  private readonly autoplayInterval$ = new Subject<void>();
  private readonly destroyed$ = new Subject<void>();
  private autoplayStarted = false;
  public trackbyFn: TrackByFunction<HeroViewModel> = (index, item) => `${item.resolved}-${item.items?.length ?? 0}`;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly screenService: ScreenService,
    private readonly translateService: TranslateService,
    private readonly clientConfigService: ClientConfigService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(PLATFORM_ID) private readonly platformId
  ) {
    this.isMobile$ = combineLatest([this.screenService.isXS$, this.screenService.isSM$]).pipe(map(([isXS, isSM]) => isXS || isSM));
    this.isLarge$ = combineLatest([this.screenService.isLG$, this.screenService.isXL$]).pipe(map(([isLG, isXL]) => isLG || isXL));
  }

  public ngOnInit(): void {
    this.startAutoPlay();
  }

  get isArabic(): boolean {
    return this.translateService.currentLang === 'ar';
  }

  get currentIndex(): number {
    return ((this.slideCounter % this.vm.items.length) + this.vm.items.length) % this.vm.items.length;
  }

  get isSingleHeroItem(): boolean {
    return this.vm.items.length === 1;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private startAutoPlay() {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    if (this.autoplayStarted) {
      return;
    }
    this.autoplayStarted = true;

    this.autoplayInterval$
      .pipe(
        switchMap(() => interval(this.clientConfigService.heroAutomaticRollDelayMilliseconds || AUTO_PLAY_INTERVAL)),
        filter(() => {
          if (!this.isBrowserTabVisible) {
            this.resetAutoPlayTimer();
            return false;
          }
          return this.vm.items.length > 1;
        }),
        // eslint-disable-next-line rxjs/no-ignored-takewhile-value
        takeWhile(() => this.autoplayStarted),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.nextItem();
        this.cdr.markForCheck();
      });
    this.autoplayInterval$.next();
  }

  private stopAutoPlay() {
    this.autoplayStarted = false;
  }

  private resetAutoPlayTimer() {
    this.autoplayInterval$.next();
  }

  private startTransition() {
    this.transitionInProgress = true;
    setTimeout(() => {
      this.transitionInProgress = false;
    }, TRANSITION_TIME);
  }

  public onHeroContainerMouseEnter() {
    this.stopAutoPlay();
  }

  public onHeroContainerMouseLeave() {
    this.startAutoPlay();
  }

  public onNextClick() {
    this.emitEventForHeroItemChangeByTheUser(this.vm.items[this.currentIndex]); // need for gtm
    if (this.isArabic) {
      this.prevItem();
    } else {
      this.nextItem();
    }
    this.resetAutoPlayTimer();
  }

  public onPrevClick() {
    this.emitEventForHeroItemChangeByTheUser(this.vm.items[this.currentIndex]); // need for gtm
    if (this.isArabic) {
      this.nextItem();
    } else {
      this.prevItem();
    }
    this.resetAutoPlayTimer();
  }

  public onSwipeLeft() {
    if (this.isSingleHeroItem) {
      return;
    }
    if (this.isArabic) {
      this.prevItem();
    } else {
      this.nextItem();
    }
    this.resetAutoPlayTimer();
  }

  public onSwipeRight() {
    if (this.isSingleHeroItem) {
      return;
    }
    if (this.isArabic) {
      this.nextItem();
    } else {
      this.prevItem();
    }
    this.resetAutoPlayTimer();
  }

  public onStepIndicatorClick(index: number) {
    this.emitEventForHeroItemChangeByTheUser(this.vm.items[index]); // need for gtm
    if (this.transitionInProgress) {
      return;
    }
    this.startTransition();
    this.prevIndex = this.currentIndex;
    this.slideCounter = index;
    this.resetAutoPlayTimer();
  }

  public onHeroItemButtonClick({ actionType, videoLength, seriesTitle, buttonText }): void {
    const currentHeroItem = this.vm.items[this.currentIndex];
    this.heroItemButtonClick.emit({
      actionType,
      heroItem: currentHeroItem,
      index: this.currentIndex,
      buttonText,
    }); // need for gtm
  }

  public onHeroItemGenreClick({ genreName, videoLength }): void {
    this.heroItemGenreClick.emit({ heroItem: this.vm.items[this.currentIndex], genreName, videoLength });
  }

  private nextItem(): void {
    if (this.transitionInProgress) {
      return;
    }
    this.startTransition();
    this.prevIndex = this.currentIndex;
    this.slideCounter++;
  }

  private prevItem(): void {
    if (this.transitionInProgress) {
      return;
    }
    this.startTransition();
    this.prevIndex = this.currentIndex;
    this.slideCounter--;
  }

  public get visible(): boolean {
    return !!this.vm && (!this.vm.resolved || (this.vm.resolved && this.vm.items && this.vm.items.length > 0));
  }

  private get isBrowserTabVisible(): boolean {
    return this.document.visibilityState === 'visible';
  }

  private emitEventForHeroItemChangeByTheUser(heroItem: HeroItemViewModel): void {
    this.heroItemChangeByTheUser.emit({ heroItem });
  }
}
